/**
 * Sprint 26.0 — the "References" section.
 *
 * Lists the references attached to a task (pages / entities / library nodes, via
 * the task_references join). "Add" opens the AddReferenceModal; the trailing ✕
 * removes the link only — the target stays in the workspace.
 */

import { useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { AddReferenceModal } from "./AddReferenceModal";
import { useTaskReferences, useRemoveTaskReference } from "../../hooks/use-task-detail";
import type { TaskReference } from "../../../lib/types";

export interface ReferencesSectionProps {
  taskId: string;
  agentId: string;
}

/** Where a reference target opens, keyed by its kind. */
function targetHref(ref: TaskReference): string {
  switch (ref.target_kind) {
    case "page":
    case "library_node":
      return `/library/${ref.target_id}`;
    case "entity":
      return `/library/${ref.target_id}`;
    default:
      return `/library/${ref.target_id}`;
  }
}

function targetIcon(ref: TaskReference): string {
  switch (ref.target_kind) {
    case "page":
      return "ti-file-text";
    case "entity":
      return "ti-database";
    case "library_node":
      return "ti-folder";
    default:
      return "ti-link";
  }
}

export function ReferencesSection({ taskId, agentId }: ReferencesSectionProps) {
  const navigate = useNavigate();
  const { data: references = [] } = useTaskReferences(taskId);
  const remove = useRemoveTaskReference();
  const [modal, setModal] = useState(false);

  return (
    <section style={{ marginBottom: 16 }} data-testid="references-section">
      <div style={headerRow}>
        <span style={label}>References · {references.length}</span>
        <button type="button" style={addBtn} onClick={() => setModal(true)}>
          <i className="ti ti-plus" style={{ fontSize: 12 }} aria-hidden="true" /> Add
        </button>
      </div>
      {references.length === 0 ? (
        <div style={empty}>No references. Click "Add" to link a page, entity, or document.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {references.map((r) => (
            <ReferenceRow
              key={`${r.target_kind}:${r.target_id}`}
              reference={r}
              onOpen={() => navigate(targetHref(r))}
              onRemove={() => remove.mutate({ taskId, kind: r.target_kind, targetId: r.target_id })}
            />
          ))}
        </div>
      )}
      {modal && <AddReferenceModal taskId={taskId} agentId={agentId} onClose={() => setModal(false)} />}
    </section>
  );
}

function ReferenceRow({ reference, onOpen, onRemove }: { reference: TaskReference; onOpen: () => void; onRemove: () => void }) {
  const subtitle = [reference.surface, reference.status].filter(Boolean).join(" · ");
  return (
    <div style={row}>
      <i className={`ti ${targetIcon(reference)}`} style={{ fontSize: 15, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
      <button type="button" style={titleBtn} onClick={onOpen}>
        <span style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>
          {reference.label ?? reference.target_id}
        </span>
        {subtitle && <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{subtitle}</span>}
      </button>
      <button type="button" style={removeBtn} aria-label="Remove reference" title="Remove reference (keeps the target)" onClick={onRemove}>
        <i className="ti ti-x" style={{ fontSize: 13 }} aria-hidden="true" />
      </button>
    </div>
  );
}

const headerRow: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 6,
};

const label: CSSProperties = { fontSize: 11, fontWeight: 500, color: "var(--color-text-secondary)" };

const addBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
};

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  padding: "9px 11px",
};

const titleBtn: CSSProperties = {
  flex: 1,
  minWidth: 0,
  textAlign: "left",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  padding: 0,
};

const removeBtn: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  padding: 2,
};

const empty: CSSProperties = {
  fontSize: 13,
  fontStyle: "italic",
  color: "var(--color-text-tertiary)",
};
